<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, useTemplateRef, watch } from 'vue';
import { uniqueId } from 'lodash-es';
import SearchIcon from '@/assets/icons/shared/search.svg';
import CircleCheckIcon from '@/assets/icons/shared/circle/check.svg';
import { CounterpartyApi } from '@/modules/counterparty/api/counterparty.api.ts';
import type { CounterpartyInnResult } from '@/modules/counterparty/types/counterparty-inn-result.type.ts';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import Validator from '@/modules/common/utils/validator.ts';
import { COUNTERPARTY_DIGITS_MASK } from '@/modules/counterparty/counterparty-master/consts/digits-mask.ts';
import { DUPLICATE_WARNING_MAX_HEIGHT_PX } from '@/modules/counterparty/counterparty-master/consts/legal-data-layout.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import { ValueUtil } from '@/modules/common/utils/value.util.ts';
import { useFormTooltips } from '@/modules/ui/composables/use-form-tooltips.ts';
import UiInput from '@/modules/ui/components/ui/UiInput.vue';
import UiButtonIcon from '@/modules/ui/components/ui/UiButtonIcon.vue';
import UiSpinner from '@/modules/ui/components/ui/UiSpinner.vue';
import UiMessage from '@/modules/ui/components/ui/UiMessage.vue';
import UiField from '@/modules/ui/components/ui/UiField.vue';

const INN_PLACEHOLDER = 'counterparty.master.steps.name.inn.label';
const DUPLICATE_HIDE_MS = 3000;

const inn = defineModel<string>('inn', { required: true });
const loading = defineModel<boolean>('loading', { default: false });
const duplicate = defineModel<boolean>('duplicate', { default: false });

const props = defineProps<{
  applied?: boolean;
}>();

const emit = defineEmits<{
  apply: [value: CounterpartyInnResult];
  clearLegalData: [];
}>();

const { signal, abort } = useAbort();
const { tooltipsDisabled, onInput } = useFormTooltips();
const fieldId = uniqueId(IdPrefix.Control);
const inputRef = useTemplateRef<InstanceType<typeof UiInput>>('input');
const innTooltip = computed(() => ValueUtil.placeholderTooltip(INN_PLACEHOLDER, inn.value));

const isSearchSuccess = ref(Boolean(props.applied));
let hideDuplicateTimeoutId: ReturnType<typeof setTimeout> | null = null;

function clearHideDuplicateTimeout() {
  if (hideDuplicateTimeoutId === null) {
    return;
  }

  clearTimeout(hideDuplicateTimeoutId);
  hideDuplicateTimeoutId = null;
}

function resetDuplicate() {
  clearHideDuplicateTimeout();
  duplicate.value = false;
}

function scheduleHideDuplicate() {
  clearHideDuplicateTimeout();
  hideDuplicateTimeoutId = setTimeout(() => {
    hideDuplicateTimeoutId = null;
    duplicate.value = false;
  }, DUPLICATE_HIDE_MS);
}

watch(
  () => props.applied,
  (value) => {
    isSearchSuccess.value = Boolean(value);
  }
);

watch(inn, (value, prev) => {
  const next = (value ?? '').replace(/\D/gu, '');
  const previous = (prev ?? '').replace(/\D/gu, '');
  if (next === previous) {
    return;
  }

  resetDuplicate();
  isSearchSuccess.value = false;
});

onUnmounted(clearHideDuplicateTimeout);

async function checkDuplicate(query: string, requestSignal: AbortSignal) {
  const exists = await CounterpartyApi.checkInnExists(query, requestSignal);
  if (requestSignal.aborted) {
    return;
  }

  duplicate.value = exists;
  if (exists) {
    emit('clearLegalData');
    scheduleHideDuplicate();
  }
}

async function search() {
  const query = inn.value.trim();
  if (!query || loading.value) {
    return;
  }

  const formatError = Validator.inn(query);
  if (formatError) {
    notification.error(formatError);
    return;
  }

  const restoreFocus = Boolean(inputRef.value?.isFocused);
  abort();
  resetDuplicate();
  loading.value = true;
  isSearchSuccess.value = false;
  const requestSignal = signal.value;

  try {
    await checkDuplicate(query, requestSignal);
    if (requestSignal.aborted || duplicate.value) {
      return;
    }

    const found = await CounterpartyApi.searchByInn(query, requestSignal);
    if (requestSignal.aborted) {
      return;
    }

    if (!found) {
      notification.error('counterparty.master.steps.name.inn.notFound');
      return;
    }

    if (inn.value.trim() !== query) {
      return;
    }

    isSearchSuccess.value = true;
    emit('apply', found);
  } catch (error) {
    if (requestSignal.aborted || !ErrorUtil.isUnexpected(error)) {
      return;
    }
    notification.error('counterparty.master.steps.name.inn.error', error);
  } finally {
    if (requestSignal.aborted) {
      return;
    }

    loading.value = false;
    if (!restoreFocus) {
      return;
    }

    await nextTick();
    inputRef.value?.focus();
  }
}

function onKeydown(event: KeyboardEvent) {
  if (event.key !== 'Enter') {
    return;
  }

  event.preventDefault();
  void search();
}
</script>

<template>
  <div
    class="inn-search-block"
    :style="{ '--duplicate-warning-max-height': `${DUPLICATE_WARNING_MAX_HEIGHT_PX}px` }"
  >
    <UiField
      :id="fieldId"
      stretch
      with-tooltip
      :tooltip="innTooltip"
      :tooltip-disabled="tooltipsDisabled"
    >
      <UiInput
        :id="fieldId"
        ref="input"
        v-model="inn"
        :placeholder="INN_PLACEHOLDER"
        :mask="COUNTERPARTY_DIGITS_MASK.inn"
        :disabled="loading"
        autocomplete="off"
        inputmode="numeric"
        no-clear
        @keydown="onKeydown"
        @update:model-value="onInput"
      >
        <template #postfix>
          <UiSpinner
            v-if="loading"
            class="status"
            size="s"
          />
          <CircleCheckIcon
            v-else-if="isSearchSuccess"
            class="status status-icon"
          />

          <UiButtonIcon
            class="action"
            color="gray"
            size="s"
            tabindex="-1"
            :disabled="loading || !inn.trim()"
            @mousedown.stop
            @click.stop.prevent="search"
          >
            <SearchIcon />
          </UiButtonIcon>
        </template>
      </UiInput>
    </UiField>

    <Transition name="duplicate-warning">
      <div
        v-if="duplicate"
        class="duplicate-warning"
      >
        <UiMessage type="warning">
          {{ $t('counterparty.master.steps.name.inn.duplicate') }}
        </UiMessage>
      </div>
    </Transition>
  </div>
</template>

<style scoped lang="scss">
.inn-search-block {
  display: flex;
  flex-direction: column;
}

.status {
  flex-shrink: 0;
  margin-left: 8px;
}

.status-icon {
  width: 16px;
  height: 16px;
  fill: var(--color-green-800);
}

.action {
  flex-shrink: 0;
  margin-left: 12px;
  white-space: nowrap;
}

.duplicate-warning {
  margin-top: 8px;
  max-height: var(--duplicate-warning-max-height);
  overflow: hidden;
}

.duplicate-warning-enter-active,
.duplicate-warning-leave-active {
  transition:
    opacity var(--transition),
    max-height var(--transition),
    margin-top var(--transition);
}

.duplicate-warning-enter-from,
.duplicate-warning-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: 0;
}
</style>
